import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import AdminLayout from '../components/AdminLayout';
import {
  Search,
  Globe,
  FileText,
  ExternalLink,
  Check,
  AlertCircle,
  RefreshCw,
  Copy,
} from 'lucide-react';
import '../styles/seo.css';

const SEOPage = () => {
  const { token } = useAuth();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/content/pages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPages(data);
        if (data.length > 0 && !selectedPage) {
          setSelectedPage(data[0]);
        }
      }
    } catch (err) {
      setError('Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  const updateSEO = (field, value) => {
    if (!selectedPage) return;
    setSelectedPage(prev => ({
      ...prev,
      seo: {
        ...prev.seo,
        [field]: value
      }
    }));
  };

  const saveSEO = async () => {
    if (!selectedPage) return;
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/api/content/pages/${selectedPage.page_slug}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ seo: selectedPage.seo })
      });

      if (response.ok) {
        setSuccess('SEO settings saved successfully!');
        fetchPages();
      } else {
        setError('Failed to save SEO settings');
      }
    } catch (err) {
      setError('Failed to save SEO settings');
    } finally {
      setSaving(false);
    }
  };

  const generateSitemap = () => {
    const sitemapContent = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${pages.filter(p => p.is_published && p.is_enabled).map(page => `  <url>
    <loc>https://voxitconsulting.media/${page.page_slug === 'homepage' ? '' : page.page_slug}</loc>
    <lastmod>${new Date(page.updated_at).toISOString().split('T')[0]}</lastmod>
    <priority>${page.page_slug === 'homepage' ? '1.0' : '0.8'}</priority>
  </url>`).join('\n')}
</urlset>`;

    navigator.clipboard.writeText(sitemapContent);
    setSuccess('Sitemap copied to clipboard!');
  };

  const getSEOScore = (seo) => {
    let score = 0;
    if (seo?.title && seo.title.length > 10 && seo.title.length <= 60) score += 25;
    if (seo?.description && seo.description.length > 50 && seo.description.length <= 160) score += 25;
    if (seo?.keywords && seo.keywords.split(',').length >= 3) score += 25;
    if (seo?.og_title && seo?.og_description) score += 25;
    return score;
  };

  const getScoreColor = (score) => {
    if (score >= 75) return 'green';
    if (score >= 50) return 'orange';
    return 'red';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="seo-loading">
          <div className="loading-spinner" />
          <p>Loading SEO data...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="seo-page">
        <div className="seo-header">
          <div>
            <h2>SEO Management</h2>
            <p>Optimize your pages for search engines</p>
          </div>
          <button className="btn-secondary" onClick={generateSitemap}>
            <Copy size={16} /> Copy Sitemap XML
          </button>
        </div>

        {success && (
          <div className="message success">
            <Check size={18} /> {success}
          </div>
        )}
        {error && (
          <div className="message error">
            <AlertCircle size={18} /> {error}
          </div>
        )}

        <div className="seo-layout">
          {/* Pages List */}
          <div className="seo-sidebar">
            <h3>Pages</h3>
            <div className="pages-list">
              {pages.map((page) => (
                <button
                  key={page.id}
                  className={`page-item ${selectedPage?.id === page.id ? 'active' : ''}`}
                  onClick={() => setSelectedPage(page)}
                >
                  <FileText size={16} />
                  <span>{page.page_name}</span>
                  <span 
                    className={`score-badge ${getScoreColor(getSEOScore(page.seo))}`}
                  >
                    {getSEOScore(page.seo)}%
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* SEO Editor */}
          <div className="seo-editor">
            {selectedPage ? (
              <>
                <div className="editor-header">
                  <h3>{selectedPage.page_name}</h3>
                  <div className="score-display">
                    <span>SEO Score:</span>
                    <span className={`score ${getScoreColor(getSEOScore(selectedPage.seo))}`}>
                      {getSEOScore(selectedPage.seo)}%
                    </span>
                  </div>
                </div>

                {/* Preview */}
                <div className="seo-preview">
                  <h4>Search Preview</h4>
                  <div className="preview-card">
                    <div className="preview-url">
                      voxitconsulting.media/{selectedPage.page_slug === 'homepage' ? '' : selectedPage.page_slug}
                    </div>
                    <div className="preview-title">
                      {selectedPage.seo?.title || selectedPage.page_name}
                    </div>
                    <div className="preview-description">
                      {selectedPage.seo?.description || 'No description set'}
                    </div>
                  </div>
                </div>

                {/* SEO Fields */}
                <div className="seo-fields">
                  <div className="form-group">
                    <label>
                      Meta Title
                      <span className={`char-count ${(selectedPage.seo?.title?.length || 0) > 60 ? 'over' : ''}`}>
                        {selectedPage.seo?.title?.length || 0}/60
                      </span>
                    </label>
                    <input
                      type="text"
                      value={selectedPage.seo?.title || ''}
                      onChange={(e) => updateSEO('title', e.target.value)}
                      placeholder="Page title for search engines"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Meta Description
                      <span className={`char-count ${(selectedPage.seo?.description?.length || 0) > 160 ? 'over' : ''}`}>
                        {selectedPage.seo?.description?.length || 0}/160
                      </span>
                    </label>
                    <textarea
                      value={selectedPage.seo?.description || ''}
                      onChange={(e) => updateSEO('description', e.target.value)}
                      placeholder="Brief description for search results"
                      rows={3}
                    />
                  </div>

                  <div className="form-group">
                    <label>Keywords</label>
                    <input
                      type="text"
                      value={selectedPage.seo?.keywords || ''}
                      onChange={(e) => updateSEO('keywords', e.target.value)}
                      placeholder="keyword1, keyword2, keyword3"
                    />
                    <span className="help-text">Separate keywords with commas</span>
                  </div>

                  <div className="form-group">
                    <label>Canonical URL</label>
                    <input
                      type="text"
                      value={selectedPage.seo?.canonical_url || ''}
                      onChange={(e) => updateSEO('canonical_url', e.target.value)}
                      placeholder="https://voxitconsulting.media/page"
                    />
                  </div>

                  <h4>Social Media (Open Graph)</h4>

                  <div className="form-group">
                    <label>OG Title</label>
                    <input
                      type="text"
                      value={selectedPage.seo?.og_title || ''}
                      onChange={(e) => updateSEO('og_title', e.target.value)}
                      placeholder="Title for social shares"
                    />
                  </div>

                  <div className="form-group">
                    <label>OG Description</label>
                    <textarea
                      value={selectedPage.seo?.og_description || ''}
                      onChange={(e) => updateSEO('og_description', e.target.value)}
                      placeholder="Description for social shares"
                      rows={2}
                    />
                  </div>
                </div>

                <div className="editor-actions">
                  <button 
                    className="btn-primary" 
                    onClick={saveSEO}
                    disabled={saving}
                  >
                    {saving ? <RefreshCw size={16} className="spinner" /> : <Check size={16} />}
                    {saving ? 'Saving...' : 'Save SEO Settings'}
                  </button>
                </div>
              </>
            ) : (
              <div className="no-selection">
                <Globe size={48} />
                <p>Select a page to edit SEO settings</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default SEOPage;
